import { GraphQLClient, type RequestMiddleware } from 'graphql-request'
import { isNonNullish } from 'remeda'
import { z } from 'zod'

import { logger } from '@district-core/logger'

import { STAGE } from '#@/platform/env/constants/app'
import { config } from '#@/platform/networking/client/lib/config'
import { initClient } from '#@/platform/networking/client/lib/stubs/initClient.stub'
import type { Extension } from '#@/platform/networking/client/types/extension'

// <init and validate>
initClient()
if (!config.initialized) {
  throw new Error('Client not initialized: was initClient called or stubbed?')
}
// </init and validate>

/** Extends `GraphQLClient` to support chained middleware and extensions. */
export class ExtendableGraphQLClient extends GraphQLClient {
  private readonly url_: string
  private readonly extensions: readonly Extension[] = [trace]
  private readonly middleware: RequestMiddleware[] = []
  private readonly log: ReturnType<typeof logger>

  constructor(url: string, extensions: Extension[] = []) {
    const fetchImpl = config.customFetch ?? fetch
    super(url, {
      fetch: Object.assign(
        (input: Parameters<typeof fetch>[0], init: Parameters<typeof fetch>[1]) =>
          loggedFetch(this.log, fetchImpl)(input, init),
        fetchImpl
      ),

      requestMiddleware: async (request_) => {
        const headers = new Headers(request_.headers)
        for (const [key, value] of Object.entries(await config.utilities.getClientHeaders())) {
          headers.set(key, value)
        }
        let request: typeof request_ = { ...request_, headers }

        for (const middleware of this.middleware) {
          // oxlint-disable-next-line no-await-in-loop -- middleware must run in registration order
          request = await middleware(request)
        }
        return request
      },
    })

    this.url_ = url
    this.extensions = Object.freeze([...this.extensions, ...extensions])
    for (const extension of this.extensions) {
      extension.apply(this)
    }

    this.log = logger('🔀', ...this.extensions.map((e) => e.tag).filter(isNonNullish))
  }

  /** Adds request middleware that runs for every request. */
  addMiddleware(middleware: RequestMiddleware) {
    this.middleware.push(middleware)
  }

  /** Returns a client with the extension. */
  with(extension: Extension) {
    return new ExtendableGraphQLClient(this.url_, [...this.extensions, extension])
  }
}

export const auth: Extension = {
  tag: 'auth',
  apply(client) {
    client.addMiddleware(async (request) => {
      request.credentials = 'include'

      const headers = new Headers(request.headers)
      for (const [key, value] of Object.entries(await config.utilities.getAuthHeaders())) {
        headers.set(key, value)
      }
      return { ...request, headers }
    })
  },
} as const

const trace: Extension = {
  apply(client) {
    client.addMiddleware((request) => {
      const headers = new Headers(request.headers)
      // headers.set('traceparent', traceparent.make().toString())
      return { ...request, headers }
    })
  },
} as const

/** Creates a fetch wrapper that logs requests and responses when `NEXT_PUBLIC_CLIENT_LOG_FETCH` enables it. */
function loggedFetch(log: ReturnType<typeof logger>, fetchImpl: typeof fetch) {
  return Object.assign(async (input: Parameters<typeof fetch>[0], init: Parameters<typeof fetch>[1]) => {
    if (!clientLogFetch) {
      return fetchImpl(input, init)
    }

    const startTime = performance.now()

    try {
      const result = await fetchImpl(input, init)
      const duration = performance.now() - startTime

      log.info(
        ...[
          ++requestCount,
          clientLogFetch === true && init,
          clientLogFetch !== true && init && 'operationName' in init ? init.operationName : undefined,
          result.status === 200 ? '' : result.status,
          result.statusText === 'OK' ? '' : result.statusText,
          `(${duration.toFixed(0)}ms)`,
        ].filter(Boolean)
      )

      return result
    } catch (error) {
      const duration = performance.now() - startTime
      log.logger('Error').info(`(${duration.toFixed(0)}ms)`, error)
      throw error
    }
  }, fetch)
}

const clientLogFetch = z
  .stringbool()
  .or(z.literal('request'))
  .default(STAGE === 'development' ? 'request' : false)
  // oxlint-disable-next-line no-process-env
  .parse(process.env.NEXT_PUBLIC_CLIENT_LOG_FETCH)

export const client = new ExtendableGraphQLClient('https://not-configured-yet')

let requestCount = 0
